import { useRef, useState } from 'react';
import { Lightbox } from './Lightbox';
import { SHOWCASE_PHOTOS, SHOWCASE_VIDEO } from '@/content/showcase';

export function Showcase() {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const images = SHOWCASE_PHOTOS.map((p) => p.src);

  return (
    <section aria-label="Showcase" className="-mx-4">
      <HeroVideo />
      <div className="px-4 mt-5">
        <header className="flex items-baseline justify-between mb-3">
          <h2 className="headline text-[20px] uppercase tracking-[0.02em]">Моменты</h2>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
            {SHOWCASE_PHOTOS.length} фото
          </span>
        </header>
        <PhotoMosaic
          photos={SHOWCASE_PHOTOS}
          onOpen={(i) => setLightboxIdx(i)}
        />
      </div>
      {lightboxIdx !== null ? (
        <Lightbox images={images} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      ) : null}
    </section>
  );
}

function HeroVideo() {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      void v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div className="relative aspect-[9/16] max-h-[70svh] w-full overflow-hidden bg-black">
      <video
        ref={ref}
        src={SHOWCASE_VIDEO.src}
        poster={SHOWCASE_VIDEO.poster}
        className="w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        onClick={togglePlay}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
        <div className="text-white drop-shadow">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.24em] text-white/70">
            JohnCalliano Lounge
          </div>
          <div className="mt-1.5 headline text-[20px] uppercase tracking-[0.015em] leading-tight max-w-[15rem]">
            Киберпанк-лаунж в центре Москвы
          </div>
        </div>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? 'unmute' : 'mute'}
          className="pointer-events-auto w-9 h-9 rounded-full bg-black/40 backdrop-blur text-white inline-flex items-center justify-center text-sm"
        >
          {muted ? '🔇' : '🔊'}
        </button>
      </div>
      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? 'pause' : 'play'}
        className={
          'absolute inset-0 m-auto w-14 h-14 rounded-full bg-white/15 backdrop-blur text-white text-xl transition-opacity ' +
          (playing ? 'opacity-0' : 'opacity-100')
        }
      >
        ▶
      </button>
      <div className="absolute bottom-4 left-4 right-4 text-white/90 font-mono text-[10px] tracking-[0.14em] uppercase flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{
              background: 'var(--ember)',
              boxShadow: '0 0 8px 1px rgba(194,58,46,0.65)',
            }}
          />
          LIVE ATMOSPHERE
        </span>
        <span className="ml-auto text-white/55">ул. Солянка, 1/2с1</span>
      </div>
    </div>
  );
}

function PhotoMosaic({
  photos,
  onOpen,
}: {
  photos: typeof SHOWCASE_PHOTOS;
  onOpen: (index: number) => void;
}) {
  return (
    <div className="grid grid-cols-6 auto-rows-[90px] gap-2 sm:auto-rows-[120px] sm:gap-3">
      {photos.map((p, i) => {
        const { colSpan, rowSpan } = tileShape(p.span, i);
        return (
          <button
            type="button"
            key={p.src}
            onClick={() => onOpen(i)}
            aria-label={`Открыть фото ${i + 1}`}
            className={
              'relative overflow-hidden rounded-xl bg-surface group ' +
              `${colSpan} ${rowSpan}`
            }
          >
            <img
              src={p.src}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04] group-active:scale-[0.98]"
            />
            <span className="absolute inset-0 bg-black/0 group-active:bg-black/10 transition-colors" />
          </button>
        );
      })}
    </div>
  );
}

function tileShape(
  span: ShowcaseSpan | undefined,
  i: number,
): { colSpan: string; rowSpan: string } {
  // 6-col grid: produce varied shapes without overlapping.
  // tall = 3 cols × 2 rows, wide = 6 cols × 2 rows, square = 3 cols × 2 rows,
  // with a small variation for visual rhythm.
  if (span === 'wide') return { colSpan: 'col-span-6', rowSpan: 'row-span-2' };
  if (span === 'tall') return { colSpan: 'col-span-3', rowSpan: 'row-span-3' };
  // alternate square sizes so the grid breathes
  return i % 2 === 0
    ? { colSpan: 'col-span-3', rowSpan: 'row-span-2' }
    : { colSpan: 'col-span-3', rowSpan: 'row-span-2' };
}

type ShowcaseSpan = NonNullable<(typeof SHOWCASE_PHOTOS)[number]['span']>;
